import { useState } from "react";
import { useRoles, useRole, useCreateRole } from "../hooks/useRoles";
import type { RoleDetail } from "../hooks/useRoles";
import { useMigrationAck } from "../hooks/useMigrationAck";
import { RoleEditor } from "../components/RoleEditor";
import "../admin.css";
import "./RoleManagementPage.css";

export function RoleManagementPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const { roles, isLoading, error, refetch } = useRoles();
  const { role: selectedRole, isLoading: roleLoading, refetch: refetchRole } = useRole(selectedId);
  const { create, isSubmitting } = useCreateRole();
  const { ackedAt, ack, isLoading: ackLoading } = useMigrationAck();

  const handleCreate = async () => {
    if (!createName.trim() || isSubmitting) return;
    const newRole = await create({ name: createName.trim(), description: createDesc.trim() });
    setShowCreate(false);
    setCreateName("");
    setCreateDesc("");
    refetch();
    setSelectedId(newRole.id);
  };

  const handleDeleted = () => {
    setSelectedId(null);
    refetch();
  };

  const handleRefreshRole = () => {
    refetchRole();
    refetch();
  };

  // #73 — when cloning a system role, prefill the Create dialog with
  // a "Copy of …" name and the original description. Permissions still
  // need to be re-authored in the matrix because the create endpoint
  // only accepts name + description.
  const handleClone = (role: RoleDetail) => {
    setCreateName(`Copy of ${role.name}`);
    setCreateDesc(role.description);
    setShowCreate(true);
  };

  return (
    <div className="admin-page form-compact">
      {!ackLoading && ackedAt === null && (
        <div className="rm-migration-banner" role="status">
          <span>
            Permissions vocabulary updated. Existing roles have been remapped
            to the new model — please review each role&apos;s permissions. Some{" "}
            <code>admin</code> permissions were expanded to multiple specific
            capabilities.
          </span>
          <button className="rm-migration-banner-dismiss" onClick={ack}>
            Dismiss
          </button>
        </div>
      )}
      <div className="admin-header">
        <h1 className="admin-title">Role Management</h1>
        <div className="rm-header-actions">
          <button className="rb-btn rb-btn-save" onClick={() => setShowCreate(true)}>
            + Create Role
          </button>
        </div>
      </div>

      <div className="rm-layout">
        {/* Role list panel */}
        <div className="rm-list">
          {isLoading && (
            <div className="admin-loading"><div className="loading-spinner" /></div>
          )}

          {error && <div className="admin-error">Error: {error}</div>}

          {!isLoading && !error && roles.length === 0 && (
            <div className="rm-empty">
              No roles found.{" "}
              <button className="jt-link" onClick={() => setShowCreate(true)}>
                Create one
              </button>
            </div>
          )}

          {roles.map((r) => (
            <div
              key={r.id}
              className={`rm-card ${selectedId === r.id ? "rm-card--selected" : ""}`}
              onClick={() => setSelectedId(r.id)}
            >
              <div className="rm-card-header">
                <span className="rm-card-name">{r.name}</span>
                {r.isSystemDefault && <span className="rm-badge rm-badge--system">System</span>}
              </div>
              {r.description && (
                <div className="rm-card-desc">{r.description}</div>
              )}
              <div className="rm-card-meta">
                <span>{r._count.permissions} permission{r._count.permissions !== 1 ? "s" : ""}</span>
                <span>&middot;</span>
                <span>{r._count.assignments} user{r._count.assignments !== 1 ? "s" : ""}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Editor panel */}
        {!selectedId ? (
          <div className="rm-placeholder">
            Select a role to edit permissions
          </div>
        ) : roleLoading || !selectedRole ? (
          <div className="rm-editor-loading" aria-busy="true" aria-label="Loading role">
            <div className="loading-spinner" />
          </div>
        ) : (
          <RoleEditor
            role={selectedRole}
            onClose={() => setSelectedId(null)}
            onRefresh={handleRefreshRole}
            onDeleted={handleDeleted}
            onClone={handleClone}
          />
        )}
      </div>

      {/* Create dialog */}
      {showCreate && (
        <div className="dialog-overlay" onClick={() => setShowCreate(false)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()}>
            <h2 className="dialog-title">Create Role</h2>
            <div className="dialog-field">
              <label className="dialog-label" htmlFor="new-role-name">Name</label>
              <input
                id="new-role-name"
                className="rb-input"
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. Senior Keyer"
                autoFocus
              />
            </div>
            <div className="dialog-field">
              <label className="dialog-label" htmlFor="new-role-desc">Description</label>
              <textarea
                id="new-role-desc"
                className="rb-input dialog-textarea"
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                rows={2}
                placeholder="Optional description..."
              />
            </div>
            <div className="rb-footer">
              <button className="rb-btn rb-btn-cancel" onClick={() => setShowCreate(false)}>Cancel</button>
              <button
                className="rb-btn rb-btn-save"
                onClick={handleCreate}
                disabled={isSubmitting || !createName.trim()}
              >
                {isSubmitting ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
